import React, { useState, type FormEvent } from "react";

import { Modal, Accordion, Icon, Form, type AccordionTitleProps, type CheckboxProps } from "semantic-ui-react";

import { Button } from "kit/ui";
import ModalProvider from "core/ModalProvider";
import { COLORS } from "styles-js";

import { useModal } from "../../contexts/ModalContext";

import routesObject from "../../routesObject";

import CookieDetail, { type CookieItem } from "./CookieDetail.component";

import * as styles from "./CookieModal.styles.js";


const COOKIE_ITEMS: Record<"necessary" | "fullStory", CookieItem[]> = {
  necessary: [
    {
      title: "Cookie de identificación del usuario",
      nameLabel: "Nombre de la cookie",
      name: "app",
      provider: "mykredit.mx",
      expiration: "1 año",
      purpose: [ "Identificar al usuario o dispositivo dentro de la plataforma para gestionar correctamente las solicitudes de crédito, mantener la continuidad del proceso y facilitar el acceso a los servicios de MyKredit MX." ],
      source: "https://mykredit.mx/",
      processor: "MyKredit MX"
    },
    {
      title: "Cookie de preferencias de consentimiento",
      nameLabel: "Nombre de la cookie",
      name: "app",
      provider: "mykredit.mx",
      expiration: "Pendiente de confirmar",
      purpose: [ "Almacenar las preferencias de consentimiento del usuario para recordar su elección en futuras visitas al sitio web y evitar que tenga que configurar nuevamente sus preferencias de cookies." ],
      source: "https://mykredit.mx/",
      processor: "MyKredit MX"
    },
    {
      title: "Almacenamiento de autenticación y sesión",
      nameLabel: "Nombre de la tecnología",
      name: "Almacenamiento de autenticación y sesión",
      provider: "mykredit.mx",
      expiration: "24 horas",
      purpose: [ "Almacenar temporalmente la información necesaria para identificar al usuario, mantener su sesión activa y permitir el acceso seguro a su cuenta personal, así como proteger las operaciones realizadas dentro de la plataforma." ],
      source: "https://mykredit.mx/",
      processor: "MyKredit MX"
    },
    {
      title: "Protección contra bots y accesos no autorizados",
      nameLabel: "Nombre de la tecnología",
      name: "AWS WAF CAPTCHA",
      provider: "Amazon Web Services (AWS)",
      expiration: "Pendiente de confirmar",
      purpose: [ "Proteger el sitio web contra bots, accesos automatizados, intentos de fraude y posibles ataques informáticos. Esta tecnología ayuda a verificar que las solicitudes provengan de usuarios legítimos y contribuye a mantener la seguridad de la plataforma y de los procesos de solicitud de crédito." ],
      source: "https://aws.amazon.com/",
      processor: "Amazon Web Services"
    },
    {
      title: "Almacenamiento temporal de solicitudes de crédito",
      nameLabel: "Nombre de la tecnología",
      name: "Almacenamiento temporal de solicitudes de crédito",
      provider: "mykredit.mx",
      expiration: "Durante la sesión o hasta el cierre de la pestaña del navegador",
      purpose: [
        "Almacenar temporalmente la información necesaria para completar los procesos de solicitud de crédito, registro, identificación y verificación del usuario.",
        "Esto puede incluir la información relacionada con la validación de identidad, la verificación de cuentas bancarias, los códigos de verificación de un solo uso (OTP), la confirmación del número de teléfono celular y los cambios de datos de contacto, según las funcionalidades disponibles en MyKredit MX.",
        "Esta tecnología permite conservar temporalmente el avance de la solicitud y evitar que el usuario tenga que introducir nuevamente la información durante el mismo proceso."
      ],
      source: "https://mykredit.mx/",
      processor: "MyKredit MX"
    }
  ],
  fullStory: [
    {
      title: "Cookie de FullStory",
      nameLabel: "Nombre de la cookie",
      name: "fs_uid",
      provider: "mykredit.mx",
      expiration: "1 año",
      purpose: [ "Identificar a los visitantes y vincular sus sesiones de navegación dentro del sitio web mediante FullStory. Permite analizar las interacciones de los usuarios, detectar problemas de funcionamiento y usabilidad, investigar incidencias técnicas y mejorar la experiencia de navegación en MyKredit MX." ],
      source: "https://www.fullstory.com/legal/privacy-policy/",
      processor: "FullStory"
    }
  ]
};

interface CookiePermissions {
  strictlyNecessary: boolean;
  analyticsPerformance: boolean;
  functional: boolean;
  fullStoryVideo: boolean;
  advertisingPurposing: boolean;
}

interface CookieModalProps {
  approveCookiePermissions: (cookies: CookiePermissions) => void;
}

function CookieModal({ approveCookiePermissions }: CookieModalProps) {

  const modal = useModal();

  const [ isDetailed, setIsDetailed ] = useState<boolean>(false);
  const [ activeIndex, setActiveIndex ] = useState<number | null>(null);
  const [ cookiePermissions, setCookiePermissions ] = useState<CookiePermissions>({
    strictlyNecessary: true,
    analyticsPerformance: false,
    functional: false,
    fullStoryVideo: false,
    advertisingPurposing: false
  });

  const handleCloseModal = () => modal.close(CookieModal.MODAL);

  const selectAllCookies = () => {
    setCookiePermissions((prev) => ({
      ...prev,
      analyticsPerformance: true,
      functional: true,
      fullStoryVideo: true,
      advertisingPurposing: true
    }));
  };

  const acceptAllCookies = () => {
    const cookies = {
      strictlyNecessary: true,
      analyticsPerformance: true,
      functional: true,
      fullStoryVideo: true,
      advertisingPurposing: true
    };
    approveCookiePermissions(cookies);
    handleCloseModal();
  };

  const applyCookies = () => {
    approveCookiePermissions(cookiePermissions);
    handleCloseModal();
  };

  const toggleDetailed = () => setIsDetailed(!isDetailed);

  const handleAccordionTitleClick = (e: React.MouseEvent<HTMLDivElement>, titleProps: AccordionTitleProps) => {
    const { index } = titleProps;
    const numericIndex = typeof index === "number" ? index : null;
    const newIndex = activeIndex === numericIndex ? -1 : numericIndex;
    setActiveIndex(newIndex);
  };

  const handleCheckboxChange = (cookieType: keyof typeof cookiePermissions) => (event: FormEvent<HTMLInputElement>, data: CheckboxProps) => {
    event.stopPropagation();
    setCookiePermissions(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  return (
    <ModalProvider
      id={CookieModal.MODAL}
      closeOnDimmerClick={false}
      closeOnEscape={false}
      closeOnDocumentClick={false}
      size="small">
      <Modal.Content scrolling>
        <strong css={styles.modalTitle}>Política de cookies</strong>
        <div css={styles.desc}>
          <p>
            Utilizamos cookies propias y de terceros para analizar y mejorar tu experiencia de navegación. Al hacer clic en “Aceptar”, autorizas el uso de cookies. Para obtener más información, consulta nuestra Política de Cookies.
          </p>
          <a css={styles.cookiePolicyLink} href={routesObject.aboutCookies.url} target="_blank" rel="noopener noreferrer">Acerca de Cookies</a>
        </div>
        {isDetailed && (
          <div>
            <Button
              css={styles.selectAllBtn}
              size="mini"
              onClick={selectAllCookies}>
              SELECCIONAR TODO
            </Button>
            <Accordion>
              <Accordion.Title
                active={activeIndex === 0}
                index={0}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Estrictamente necesarias
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    checked
                    disabled />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                <p css={styles.categoryStatus}>Siempre activas</p>
                <p>Estas cookies y tecnologías de almacenamiento son indispensables para el funcionamiento y la seguridad de nuestro sitio web, así como para proporcionar los servicios que solicitas.</p>
                <p>Permiten gestionar el inicio de sesión, mantener la continuidad de las solicitudes de crédito, realizar procesos de identificación y verificación, proteger tu información contra accesos no autorizados y recordar tus preferencias de cookies.</p>
                <p>Estas tecnologías son necesarias para el funcionamiento del servicio y no pueden desactivarse desde este panel.</p>
              </div>
              <Accordion.Content
                active={activeIndex === 0}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.necessary.map(item => {
                  return <CookieDetail key={item.title} cookie={item} />;
                })}
              </Accordion.Content>
              <Accordion.Title
                active={activeIndex === 1}
                index={1}
                onClick={handleAccordionTitleClick}
                css={styles.titleContainer}>
                <Icon name="dropdown" />
                <strong css={styles.titleContent}>
                  Grabación de sesiones y análisis de experiencia
                </strong>
                <div css={styles.checkbox}>
                  <Form.Checkbox
                    toggle
                    onChange={handleCheckboxChange("fullStoryVideo")}
                    checked={cookiePermissions.fullStoryVideo} />
                </div>
              </Accordion.Title>
              <div css={styles.detail}>
                <p css={styles.categoryStatus}>Opcional — Requiere tu consentimiento</p>
                <p>Estas cookies nos permiten analizar cómo interactúas con nuestro sitio web mediante la herramienta FullStory, que puede registrar sesiones de navegación para identificar problemas técnicos, detectar dificultades de uso y mejorar la experiencia del usuario.</p>
                <p>La herramienta puede recopilar información relacionada con los movimientos del cursor, clics, desplazamientos, navegación entre páginas e interacciones con los elementos del sitio web.</p>
                <p>Cuando inicias sesión, también pueden asociarse a tu sesión datos identificativos como tu nombre, dirección de correo electrónico y número de teléfono celular, dependiendo de la configuración de la integración.</p>
                <p>La información recopilada se utiliza para analizar el funcionamiento de la plataforma, investigar incidencias y mejorar nuestros servicios digitales.</p>
                <p>Estas tecnologías únicamente se activarán cuando otorgues tu consentimiento. Puedes modificar o retirar tu consentimiento en cualquier momento desde la configuración de cookies.</p>
              </div>
              <Accordion.Content
                active={activeIndex === 1}
                css={styles.detail}>
                <hr />
                {COOKIE_ITEMS.fullStory.map(item => {
                  return <CookieDetail key={item.title} cookie={item} />;
                })}
              </Accordion.Content>
            </Accordion>
          </div>
        )}
        <div css={styles.buttons}>
          <Button
            id="cookie-accept-btn"
            fluid
            size="small"
            theme="orange"
            onClick={isDetailed ? applyCookies : acceptAllCookies}>
            {isDetailed ? "GUARDAR PREFERENCIAS" : "ACEPTAR"}
          </Button>
          <Button
            unstyled
            contentStyles={styles.detailBtn}
            fluid
            onClick={toggleDetailed}
            style={{
              fontSize: 14,
              color: COLORS.primary,
              width: "100%",
              marginTop: 8,
              ":hover": {
                color: COLORS.primary
              }
            }}
            content={isDetailed ? "Ocultar detalles" : "Mostrar detalles"} />

        </div>
      </Modal.Content>
    </ModalProvider>
  );
}

CookieModal.MODAL = "cookieModal";

export default CookieModal;
